import i18n from '../../../i18n';
import { toPublicErrorMessage } from '../../../shared/services/publicErrorMessage';
import { parseAuthSessionPayload } from '../../../shared/services/authRefresh';
import type { ClientAuthSession } from '../../../shared/services/authSession';
import { pathTenantHeaders } from '../../../shared/config/workspaceHost';
import type {
  CreateWorkspaceResult,
  GlobalLoginResult,
  OperatorWorkspace,
  OperatorWorkspaceDetail,
  OperatorWorkspacePage,
  PlatformAccount,
  SignUpResult,
  SignUpStartResult,
  SlugAvailability,
  WorkspaceSelection,
  WorkspaceSummary,
} from '../types/workspace';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const PICKER_SESSION_KEY = 'workspacePickerSession';

export interface StoredPickerSession {
  token: string;
  expiresAt: string;
  username: string;
  workspaces: WorkspaceSummary[];
  /** Absent for legacy members, whose picker can list workspaces but not create one. */
  accountToken?: string | null;
  isPlatformOperator?: boolean;
}

const readErrorMessage = (payload: any, fallback: string): string =>
  payload?.error?.message
  || payload?.Error?.Message
  || payload?.error?.details?.[0]?.issue
  || payload?.Error?.Details?.[0]?.Issue
  || payload?.title
  || payload?.Title
  || payload?.message
  || payload?.Message
  || fallback;

const throwHttpError = (response: Response, payload: any, fallback: string): never => {
  const fromBody = readErrorMessage(payload, '');
  const safeMessage = toPublicErrorMessage(fromBody, response.status, '');
  if (safeMessage) {
    throw new Error(safeMessage);
  }

  throw new Error(process.env.NODE_ENV === 'production' ? fallback : `${fallback} (${response.status})`);
};

const parseJson = async (response: Response): Promise<any> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const toWorkspace = (value: any): WorkspaceSummary => ({
  tenantId: String(value?.tenantId ?? ''),
  slug: String(value?.slug ?? ''),
  name: String(value?.name ?? ''),
  url: String(value?.url ?? ''),
  requiresPasswordChange: Boolean(value?.requiresPasswordChange),
});

const toAccount = (value: any): PlatformAccount | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return {
    userId: String(value.userId ?? ''),
    email: String(value.email ?? ''),
    displayName: value.displayName ? String(value.displayName) : null,
    emailVerified: Boolean(value.emailVerified),
    isPlatformOperator: Boolean(value.isPlatformOperator),
  };
};

const toSelection = (value: any): WorkspaceSelection | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const redirectUrl = String(value.redirectUrl ?? '');
  if (!redirectUrl) {
    return null;
  }

  return {
    tenantId: String(value.tenantId ?? ''),
    slug: String(value.slug ?? ''),
    url: String(value.url ?? ''),
    redirectUrl,
    handoffCode: value.handoffCode ? String(value.handoffCode) : null,
    handoffExpiresAtUtc: value.handoffExpiresAtUtc ? String(value.handoffExpiresAtUtc) : null,
  };
};

/**
 * Global login runs before any workspace is known, so it deliberately sends no tenant headers.
 */
export const globalLogin = async (username: string, password: string): Promise<GlobalLoginResult> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throwHttpError(response, payload, i18n.t('workspaces:errors.loginFailed'));
  }

  return {
    pickerToken: String(payload?.pickerToken ?? ''),
    pickerExpiresAtUtc: String(payload?.pickerExpiresAtUtc ?? ''),
    workspaces: Array.isArray(payload?.workspaces) ? payload.workspaces.map(toWorkspace) : [],
    selection: toSelection(payload?.selection),
    accountToken: payload?.accountToken ? String(payload.accountToken) : null,
    accountExpiresAtUtc: payload?.accountExpiresAtUtc ? String(payload.accountExpiresAtUtc) : null,
    account: toAccount(payload?.account),
  };
};

const toSignUpResult = (payload: any): SignUpResult | null => {
  const account = toAccount(payload?.account);
  if (!account) {
    return null;
  }

  return {
    accountToken: String(payload?.accountToken ?? ''),
    accountExpiresAtUtc: String(payload?.accountExpiresAtUtc ?? ''),
    account,
    canCreateWorkspace: Boolean(payload?.canCreateWorkspace),
  };
};

/** Starts signup. When verification is required, the account does not exist until the code is confirmed. */
export const globalSignUp = async (
  email: string,
  password: string,
  displayName: string,
): Promise<SignUpStartResult> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, displayName }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throwHttpError(response, payload, i18n.t('workspaces:errors.signUpFailed'));
  }

  if (payload?.verificationRequired) {
    return {
      verificationRequired: true,
      email: payload?.email ? String(payload.email) : email,
      account: null,
    };
  }

  const account = toSignUpResult(payload);
  if (!account) {
    throw new Error(i18n.t('workspaces:errors.signUpFailed'));
  }

  return {
    verificationRequired: false,
    email: account.account.email,
    account,
  };
};

/** Confirms the emailed code and creates the account. */
export const confirmSignUp = async (email: string, code: string): Promise<SignUpResult> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/signup/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throwHttpError(response, payload, i18n.t('workspaces:errors.signUpFailed'));
  }

  const account = toSignUpResult(payload);
  if (!account) {
    throw new Error(i18n.t('workspaces:errors.signUpFailed'));
  }

  return account;
};

export const checkWorkspaceSlug = async (
  accountToken: string,
  slug: string,
): Promise<SlugAvailability> => {
  const query = new URLSearchParams({ slug, accountToken });

  const response = await fetch(
    `${API_BASE_URL}/api/v1/auth/global/workspaces/slug-available?${query.toString()}`,
    { method: 'GET' },
  );

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.slugCheckFailed')));
  }

  return {
    slug: String(payload?.slug ?? slug),
    available: Boolean(payload?.available),
    reason: payload?.reason ? String(payload.reason) : null,
  };
};

export const createWorkspace = async (
  accountToken: string,
  name: string,
  slug: string,
): Promise<CreateWorkspaceResult> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/workspaces`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accountToken, name, slug }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.createFailed')));
  }

  const selection = toSelection(payload);
  if (!selection) {
    throw new Error(i18n.t('workspaces:errors.createFailed'));
  }

  return { ...selection, name: String(payload?.name ?? '') };
};

export const selectWorkspace = async (pickerToken: string, tenantId: string): Promise<WorkspaceSelection> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/workspaces/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pickerToken, tenantId }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.selectFailed')));
  }

  const selection = toSelection(payload);
  if (!selection) {
    throw new Error(i18n.t('workspaces:errors.selectFailed'));
  }

  return selection;
};

/**
 * Redeems the one-time code on the workspace host. `tenantId` is only needed in local development,
 * where the origin is localhost and cannot identify the workspace on its own.
 */
export const consumeWorkspaceHandoff = async (
  code: string,
  tenantId?: string | null,
): Promise<ClientAuthSession> => {
  const trimmedTenantId = tenantId?.trim();

  const response = await fetch(`${API_BASE_URL}/api/v1/auth/handoff/consume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Url': window.location.origin,
      ...pathTenantHeaders(),
      ...(trimmedTenantId ? { TenantId: trimmedTenantId, 'X-Tenant-Id': trimmedTenantId } : {}),
    },
    body: JSON.stringify({ code }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.handoffFailed')));
  }

  return parseAuthSessionPayload(payload, trimmedTenantId);
};

export const savePickerSession = (session: StoredPickerSession): void => {
  try {
    sessionStorage.setItem(PICKER_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Ignore: the picker still works for the current navigation.
  }
};

export const readPickerSession = (): StoredPickerSession | null => {
  try {
    const raw = sessionStorage.getItem(PICKER_SESSION_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredPickerSession;
    if ((!parsed?.token && !parsed?.accountToken) || !Array.isArray(parsed.workspaces)) {
      return null;
    }

    if (parsed.expiresAt && Date.parse(parsed.expiresAt) <= Date.now()) {
      clearPickerSession();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const toOperatorWorkspace = (value: any): OperatorWorkspace => ({
  tenantId: String(value?.tenantId ?? ''),
  slug: String(value?.slug ?? ''),
  name: String(value?.name ?? ''),
  url: String(value?.url ?? ''),
  ownerEmail: value?.ownerEmail ? String(value.ownerEmail) : null,
  ownerName: value?.ownerName ? String(value.ownerName) : null,
  billingStatus: String(value?.billingStatus ?? ''),
  planCode: value?.planCode ? String(value.planCode) : null,
  accessUntilUtc: value?.accessUntilUtc ? String(value.accessUntilUtc) : null,
  isSuspended: Boolean(value?.isSuspended),
  suspendedAtUtc: value?.suspendedAtUtc ? String(value.suspendedAtUtc) : null,
  suspensionReason: value?.suspensionReason ? String(value.suspensionReason) : null,
  createdAtUtc: value?.createdAtUtc ? String(value.createdAtUtc) : null,
});

const operatorRequest = async (
  accountToken: string,
  path: string,
  init?: RequestInit,
): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/operator${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accountToken}`,
      ...(init?.headers ?? {}),
    },
  });
  const payload = await parseJson(response);
  if (!response.ok) {
    throwHttpError(response, payload, i18n.t('workspaces:errors.operatorFailed'));
  }

  return payload;
};

export const listOperatorWorkspaces = async (
  accountToken: string,
  search: string,
  page: number,
  sort: 'created' | 'access' = 'created',
  direction: 'asc' | 'desc' = 'desc',
): Promise<OperatorWorkspacePage> => {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: '25',
    sort,
    direction,
  });
  const term = search.trim();
  if (term) {
    query.set('search', term);
  }

  const payload = await operatorRequest(accountToken, `/workspaces?${query.toString()}`);
  return {
    items: Array.isArray(payload?.items) ? payload.items.map(toOperatorWorkspace) : [],
    page: Number(payload?.page ?? page),
    pageSize: Number(payload?.pageSize ?? 25),
    totalCount: Number(payload?.totalCount ?? 0),
    plans: Array.isArray(payload?.plans)
      ? payload.plans.map((plan: any) => ({
        code: String(plan?.code ?? ''),
        name: String(plan?.name ?? plan?.code ?? ''),
      }))
      : [],
  };
};

const toOperatorDetail = (payload: any): OperatorWorkspaceDetail => ({
  workspace: toOperatorWorkspace(payload?.workspace),
  provider: payload?.provider ? String(payload.provider) : null,
  periodStartUtc: payload?.periodStartUtc ? String(payload.periodStartUtc) : null,
  periodEndUtc: payload?.periodEndUtc ? String(payload.periodEndUtc) : null,
  trialEndsAtUtc: payload?.trialEndsAtUtc ? String(payload.trialEndsAtUtc) : null,
  people: {
    owners: Number(payload?.people?.owners ?? 0),
    admins: Number(payload?.people?.admins ?? 0),
    members: Number(payload?.people?.members ?? 0),
  },
  charges: Array.isArray(payload?.charges)
    ? payload.charges.map((charge: any) => ({
      id: String(charge?.id ?? ''),
      planCode: String(charge?.planCode ?? ''),
      provider: String(charge?.provider ?? ''),
      status: String(charge?.status ?? ''),
      amountUsd: Number(charge?.amountUsd ?? 0),
      amountAzn: Number(charge?.amountAzn ?? 0),
      note: charge?.note ? String(charge.note) : null,
      createdAtUtc: String(charge?.createdAtUtc ?? ''),
      paidAtUtc: charge?.paidAtUtc ? String(charge.paidAtUtc) : null,
    }))
    : [],
  activity: Array.isArray(payload?.activity)
    ? payload.activity.map((entry: any) => ({
      action: String(entry?.action ?? ''),
      actorEmail: String(entry?.actorEmail ?? ''),
      detail: entry?.detail ? String(entry.detail) : null,
      createdAtUtc: String(entry?.createdAtUtc ?? ''),
    }))
    : [],
  plansHidden: Boolean(payload?.plansHidden),
});

export const getOperatorWorkspace = async (
  accountToken: string,
  tenantId: string,
): Promise<OperatorWorkspaceDetail> => {
  const payload = await operatorRequest(accountToken, `/workspaces/${tenantId}`);
  return toOperatorDetail(payload);
};

export const assignOperatorPlan = async (
  accountToken: string,
  tenantId: string,
  planCode: string,
  accessUntil: string,
  invoice?: { amountUsd: number; note: string; paid: boolean },
): Promise<OperatorWorkspaceDetail> => {
  const payload = await operatorRequest(accountToken, `/workspaces/${tenantId}/plan`, {
    method: 'POST',
    body: JSON.stringify({
      planCode,
      accessUntil,
      invoiceAmountUsd: invoice ? invoice.amountUsd : null,
      invoiceNote: invoice?.note ?? null,
      invoicePaid: invoice?.paid === true,
    }),
  });
  return toOperatorDetail(payload);
};

export const updateOperatorCharge = async (
  accountToken: string,
  tenantId: string,
  orderId: string,
  status: 'paid' | 'canceled',
): Promise<OperatorWorkspaceDetail> => {
  const payload = await operatorRequest(accountToken, `/workspaces/${tenantId}/charges/${orderId}`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
  return toOperatorDetail(payload);
};

export const setOperatorPlansVisibility = async (
  accountToken: string,
  tenantId: string,
  hidden: boolean,
): Promise<OperatorWorkspaceDetail> => {
  const payload = await operatorRequest(accountToken, `/workspaces/${tenantId}/plans-visibility`, {
    method: 'POST',
    body: JSON.stringify({ hidden }),
  });
  return toOperatorDetail(payload);
};

export const setOperatorSuspension = async (
  accountToken: string,
  tenantId: string,
  suspended: boolean,
  reason: string,
): Promise<OperatorWorkspace> => {
  const payload = await operatorRequest(accountToken, `/workspaces/${tenantId}/suspension`, {
    method: 'POST',
    body: JSON.stringify({ suspended, reason }),
  });
  return toOperatorWorkspace(payload);
};

export const openOperatorWorkspace = async (
  accountToken: string,
  tenantId: string,
  pickerToken: string,
): Promise<WorkspaceSelection> => {
  const payload = await operatorRequest(accountToken, `/workspaces/${tenantId}/open`, {
    method: 'POST',
    body: JSON.stringify({ pickerToken }),
  });
  const selection = toSelection(payload);
  if (!selection) {
    throw new Error(i18n.t('workspaces:errors.selectFailed'));
  }

  return selection;
};

export const clearPickerSession = (): void => {
  try {
    sessionStorage.removeItem(PICKER_SESSION_KEY);
  } catch {
    // Ignore.
  }
};
